<template>
  <v-card class="files-table">
    <v-card-title class="toolbar white--text">
      <h2>Files</h2>
      <v-spacer />
      <v-switch
        v-model="resultFilters.onlyTemplates"
        label="Toggle Templates/Files"
        color="white"
      >
        <template v-slot:label>
          <span class="white--text">Toggle Templates/Files</span>
        </template>
      </v-switch>
      <v-select
        v-model="category"
        primary
        dark
        solo-inverted
        flat
        append-icon="pending_actions"
        class="ma-0 mr-4"
        :items="fileCategories"
        label="File Categories"
        item-value="value"
        hide-details
        clearable
      />
      <v-btn
        dark
        color="blue"
        @click.stop="openFileDialog()"
      >
        <v-icon dark>
          add
        </v-icon>Upload
      </v-btn>
    </v-card-title>

    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        Filtered results ({{ filteredResults.length }} of {{ filteredItems.length }})
        <v-tooltip
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="primary"
              v-on="on"
              @click="resetResultsFilters"
            >
              <v-icon>
                restore
              </v-icon>
            </v-btn>
          </template>
          <span>Reset filters</span>
        </v-tooltip>
      </div>
      <v-text-field
        v-model="resultFilters.searchResults"
        class="ml-5"
        append-icon="search"
        label="filter results"
      />
    </v-card-title>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredResults"
      :loading="loading"
      :pagination.sync="pagination"
      :rows-per-page-items="rowsPerPageItems"
      item-key="created_at"
      class="elevation-1"
    >
      <template v-slot:items="props">
        <tr
          :active="props.selected"
        >
          <td>
            {{ props.item.category }}
          </td>
          <td
            align="center"
          >
            <v-icon
              v-if="props.item.template !== undefined"
              color="green"
            >
              check_circle
            </v-icon>
          </td>
          <td>
            {{ props.item.title }}
          </td>
          <td>
            {{ props.item.fileName }}
          </td>
          <td>
            {{ props.item.created_at ? formatDateSmall(props.item.created_at) : null }}
          </td>
          <td>
            {{ props.item.deleted_at ? formatDateSmall(props.item.deleted_at) : null }}
          </td>
          <td>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="teal"
                  flat
                  icon
                  v-on="on"
                  @click="download(props.item)"
                >
                  <v-icon>cloud_download</v-icon>
                </v-btn>
              </template>
              <span>download</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="blue"
                  flat
                  icon
                  v-on="on"
                  @click="preview(props.item)"
                >
                  <v-icon>preview</v-icon>
                </v-btn>
              </template>
              <span>preview</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="warning"
                  flat
                  icon
                  v-on="on"
                  @click="openEditFile(props.item)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              <span>edit</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
    <upload-file
      :dialog="isOpenFileDialog"
      :submit-action="submitCreateFile"
      :cancel-action="cancelFileModal"
    />
    <update-file
      :dialog="isOpenFileEdit"
      :file="updateFile"
      :submit-action="submitEditFile"
      :cancel-action="cancelFileModal"
    />
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import { formatDateSmall } from '@/utils/dateFormats';
import { toQueryString, setQuery } from '@/helpers';
import UploadFile from './UploadFile';
import FileCategoriesEnum from '../../enum/financialProducts/FileCategories';
import UpdateFile from './UpdateFile';

export default {
  name: 'FilesTable',
  components: {
    UpdateFile,
    UploadFile,
  },
  props: {
    files: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      category: 'agb',
      selected: [],
      updateFile: null,
      isOpenFileEdit: false,
      isOpenFileDialog: false,
      filters: {
        createdAt: [],
        state: [],
      },
      resultFilters: {
        status: null,
        searchResults: null,
        onlyTemplates: true,
      },
      headers: [
        { text: 'Category', align: 'left', value: 'category' },
        { text: 'Template', align: 'center', value: 'template' },
        { text: 'Title', align: 'left', value: 'title' },
        { text: 'FileName', align: 'left', value: 'fileName' },
        { text: 'Created at', align: 'left', value: 'created_at' },
        { text: 'Deleted at', align: 'left', value: 'deleted_at' },
        { text: 'Actions', align: 'left', value: 'actions' },
      ],
      pagination: {
        page: 1,
        rowsPerPage: 25,
        sortBy: 'created_at',
        descending: true,
      },
      rowsPerPageItems: [
        { text: '25', value: 25 },
        { text: '50', value: 50 },
        { text: '100', value: 100 },
        { text: 'All', value: -1 },
      ],
      formatDateSmall,
    };
  },
  computed: {
    filteredItems() {
      return this.files.filter(d => Object.keys(this.filters).every((f) => {
        if (this.filters[f]) {
          return (
            this.filters[f].length < 1 ||
            String(this.filters[f]).includes(d[f])
          );
        }

        return true;
      }));
    },
    filteredResults() {
      return this.filteredItems.filter((item) => {
        let searchMatched = false;
        const { searchResults } = this.resultFilters;
        if (searchResults) {
          searchMatched = JSON.stringify(item)
            .toLowerCase()
            .includes(searchResults.toLowerCase());
        }

        const { onlyTemplates } = this.resultFilters;
        const templateSearchMatched = (item.template !== undefined) === onlyTemplates;

        return (templateSearchMatched && (!searchResults || !!searchMatched));
      });
    },
    fileCategories() {
      return Object.keys(FileCategoriesEnum).map(key => FileCategoriesEnum[key]).sort();
    },
  },
  watch: {
    category: {
      immediate: false,
      handler(category, oldCategory) {
        if (category !== oldCategory) {
          setQuery(
            {
              category,
            },
            this.$route,
            this.$router,
          );
          this.refreshTable();
        }
      },
    },
  },
  methods: {
    ...mapActions([
      'getFiles',
      'uploadFile',
      'previewFile',
    ]),
    refreshTable() {
      const query = toQueryString({
        category: this.category,
      });
      if (typeof window.LIT !== 'undefined') {
        clearTimeout(window.LIT);
      }
      window.LIT = setTimeout(() => {
        this.getFiles(query);
      }, 1000);
    },
    getDefaultFilters() {
      if (this.$route.query) {
        Object.keys(this.$route.query).forEach((item) => {
          let value = this.$route.query[item];

          if (Object.prototype.hasOwnProperty.call(this.filters, item)) {
            if (typeof this.filters[item] === 'string') {
              this.filters[item] = value;
            } else if (String(this.$route.query[item]).includes(';')) {
              value = this.$route.query[item].split(';');
              this.filters[item] = value;
            } else {
              this.filters[item].push(value);
            }
          }
        });
      }
    },
    resetResultsFilters() {
      this.resultFilters = {
        onlyTemplates: true,
        status: null,
        searchResults: null,
      };
    },
    openEditFile(file) {
      this.updateFile = file;
      this.isOpenFileEdit = true;
    },
    async submitCreateFile(data) {
      try {
        await this.uploadFile(data);
        this.$emit('refreshFileTable', data.category);
        this.category = data.category;
        this.isOpenFileDialog = false;
      } catch (e) {
        this.isOpenFileDialog = true;
      }
    },
    async submitEditFile(data) {
      try {
        await this.uploadFile(data);
        this.$emit('refreshFileTable', data.category);
        this.resultFilters.onlyTemplates = !!data.template;
        this.category = data.category;
        this.isOpenFileEdit = false;
        this.updateFile = null;
      } catch (e) {
        this.isOpenFileEdit = true;
      }
    },
    openFileDialog() {
      this.isOpenFileDialog = true;
    },
    cancelFileModal() {
      this.isOpenFileDialog = false;
      this.isOpenFileEdit = false;
      this.updateFile = null;
    },
    async preview(file) {
      this.previewFile(toQueryString({
        category: file.category,
        fileName: file.fileName,
      })).then((response) => {
        const blob = new Blob([response], { type: 'application/pdf' });
        window.open(window.URL.createObjectURL(blob));
      });
    },
    async download(file) {
      this.previewFile(toQueryString({
        category: file.category,
        fileName: file.fileName,
      })).then((response) => {
        const blob = new Blob([response], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = file.fileName;
        link.click();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.files-table{
  a {
    text-decoration: none;
    color: black;
  }
  .toolbar {
    background-color: #1a4c7d;
    color: white;
  }
  .v-select{
        flex: 1;
  }

  table.v-table {
    td:not(:first-child),
    th:not(:first-child) {
      padding: 0 1% !important;
    }
  }

  .v-card__title.subheading{
    .v-icon{
      margin-right:10px;
    }
  }
  .v-input{
    &.security_wallet{
      min-width: 350px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/financialProducts/FilesTable.vue